import React, { useEffect, useState } from 'react'
import { Box, Link, TextField, Typography, Snackbar } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { isNil } from 'lodash'
import { object, string } from 'yup'

import { TextMaskPhone } from '../common/InputMasks'
import SteppedContent from '../common/SteppedContent'
import ButtonSubmit from '../common/ButtonSubmit'
import { pushSnowplow, toUserUnboardingProcessSchema } from '../../util/snowplow'
import {
  CLICKED_COMPLETE_ONBOARDING,
  CLICKED_SKIP,
  ENTERED_PHONE_NUMBER,
  VIEWED_INFORMATIONAL_SNACKBAR,
} from '../../util/events/onboardingEvents'

import { ONBOARDING_STEPS } from './onboardingUtils'

const validationSchema = object().shape({
  phoneNumber: string()
    .required('Please enter your phone number.')
    .length(10, 'Please enter a valid phone number.'),
})

const StepMobileDownload = ({ onSubmit, error, submitting, showSnackbar, setSuccess }) => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [validationError, setValidationError] = useState(null)

  useEffect(() => {
    if (showSnackbar) {
      pushSnowplow(
        toUserUnboardingProcessSchema(
          VIEWED_INFORMATIONAL_SNACKBAR,
          ONBOARDING_STEPS.MOBILE_DOWNLOAD.name,
        ),
      )
    }
  }, [showSnackbar])

  useEffect(() => {
    if (phoneNumber !== '') {
      setValidationError(null)
    }
  }, [phoneNumber])

  const handleBlur = () => {
    pushSnowplow(
      toUserUnboardingProcessSchema(
        ENTERED_PHONE_NUMBER,
        ONBOARDING_STEPS.MOBILE_DOWNLOAD.name,
        phoneNumber,
      ),
    )
  }

  const handleSkipClick = () => {
    pushSnowplow(toUserUnboardingProcessSchema(CLICKED_SKIP, ONBOARDING_STEPS.MOBILE_DOWNLOAD.name))
    setSuccess(true)
  }

  const handleSubmit = () => {
    validationSchema
      .validate({ phoneNumber })
      .then(() => {
        onSubmit(phoneNumber)
        pushSnowplow(
          toUserUnboardingProcessSchema(
            CLICKED_COMPLETE_ONBOARDING,
            ONBOARDING_STEPS.MOBILE_DOWNLOAD.name,
          ),
        )
      })
      .catch(({ message }) => {
        setValidationError(message)
        pushSnowplow(
          toUserUnboardingProcessSchema(
            CLICKED_COMPLETE_ONBOARDING,
            ONBOARDING_STEPS.MOBILE_DOWNLOAD.name,
            null,
            message,
          ),
        )
      })
  }

  return (
    <SteppedContent
      title="Get delivery updates on your phone"
      description="If you'd like to receive notifications on your phone then enter your number below
        and we'll text you a link to download the Foodsby app"
      progressText={`Step ${ONBOARDING_STEPS.MOBILE_DOWNLOAD.value} of 3`}
      action={
        <>
          <ButtonSubmit
            submitting={submitting}
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            Finish Setup
          </ButtonSubmit>
          <Box marginTop={2} marginBottom={3}>
            {error && (
              <Typography color="error">
                Sorry, there was a problem sending the text message. Please try again or skip this
                step for now.
              </Typography>
            )}
          </Box>
          <Link component="button" color="textSecondary" onClick={handleSkipClick}>
            Skip
          </Link>
        </>
      }
    >
      <TextField
        id="phoneNumber"
        name="phoneNumber"
        value={phoneNumber}
        InputProps={{
          inputComponent: TextMaskPhone,
          inputProps: {
            onValueChange: values => setPhoneNumber(values.value),
            onBlur: handleBlur,
          },
        }}
        type="tel"
        placeholder="(###) ###-####"
        variant="outlined"
        error={!isNil(validationError)}
        helperText={!isNil(validationError) && validationError}
      />
      <Snackbar open={showSnackbar}>
        <Alert severity="info">
          We&apos;ll email you when the restaurant delivers your food. Your lunch is delivered to
          either the Foodsby kiosk (where available) or the front desk in the lobby.
        </Alert>
      </Snackbar>
    </SteppedContent>
  )
}

export default StepMobileDownload
